import useClickAway from '@patrianna/shared-hooks/useClickAway'
import { getTranslationFromJson } from '@patrianna/shared-utils/prismic'
import { CalendarMonthOutline, ChevronDown } from '@patrianna-icons/material-icons'
import cx from 'classnames'
import { type MouseEvent, useRef, useState } from 'react'

import CustomRange, {
  CUSTOM_RANGE,
  DEFAULT_DATE_RANGE,
} from 'pageContainers/TransactionsPage/components/FiltersBlock/components/CustomRange'
import type { ItemProps } from 'pageContainers/TransactionsPage/components/FiltersBlock/components/DateFilter/types'
import { getDateRange, formatRange } from 'pageContainers/TransactionsPage/components/FiltersBlock/utils'
import type { DateRangeProps } from 'pageContainers/TransactionsPage/components/FiltersBlock/utils'
import content from 'prismic-translates/transactions-history-page.json'

import classes from './styles.module.scss'

const t = getTranslationFromJson([content])

type Props = {
  handleChange: (range: DateRangeProps) => void
}

const calendarIcon = <CalendarMonthOutline className={classes.calendarIcon} />

const defaultOption: ItemProps = { value: '', label: t('filter_by'), data: t('filter_by') }
const commonRangeMap: Record<string, DateRangeProps> = {
  7: getDateRange(7),
  15: getDateRange(15),
  30: getDateRange(30),
}

export default function DateFilter({ handleChange }: Props) {
  const customSelectRef = useRef<HTMLDivElement | null>(null)
  const [isShow, setIsShow] = useState<boolean>(false)
  const [dateRange, setDateRange] = useState<DateRangeProps>(DEFAULT_DATE_RANGE)
  const [selectedValue, setSelectedValue] = useState<ItemProps>(defaultOption)

  useClickAway(customSelectRef, () => setIsShow(false))

  const handleChangeFilter = (item: ItemProps): void => {
    if (item.value === CUSTOM_RANGE) {
      return null
    }

    setSelectedValue(item)
    setIsShow(false)
    handleChange(commonRangeMap[item.value])
    setDateRange(DEFAULT_DATE_RANGE)
  }

  const handleSelectValue = (option: ItemProps) => (e: MouseEvent<HTMLElement>) => {
    if (option?.value === CUSTOM_RANGE) {
      return e.stopPropagation()
    }

    handleChangeFilter(option)
  }

  const items: ItemProps[] = [
    { value: '30', label: t('last_30_days'), data: t('last_30_days') },
    { value: '15', label: t('last_15_days'), data: t('last_15_days') },
    { value: '7', label: t('last_7_days'), data: t('last_7_days') },
    {
      value: CUSTOM_RANGE,
      label: (
        <CustomRange
          handleChange={handleChange}
          selectedValue={selectedValue}
          setSelectedValue={(option) => setSelectedValue(option)}
          dateRange={dateRange}
          setDateRange={(rage) => setDateRange(rage)}
          defaultOption={defaultOption}
        />
      ),
      data: '',
    },
  ]

  const getLabel = () => {
    if (selectedValue?.value === CUSTOM_RANGE) {
      return (
        [dateRange?.startDate, dateRange?.endDate]
          ?.filter(Boolean)
          ?.map((date) => formatRange(date))
          ?.join(' - ') || t('filter_by')
      )
    }

    return selectedValue?.data
  }

  return (
    <div ref={customSelectRef} className={cx(classes.selectRoot, { [classes.selectRootShow]: isShow })}>
      <div className={classes.select} onClick={() => setIsShow(!isShow)}>
        <div className={classes.label}>
          {calendarIcon}
          <span className={classes.labelText}>{getLabel()}</span>
        </div>

        <ChevronDown className={classes.arrow} data-testid='filter-by-select' />
      </div>

      {isShow && (
        <div className={classes.options}>
          {items?.map((option) => (
            <div
              key={option.value}
              className={classes.option}
              onClick={handleSelectValue(option)}
              data-testid={`option-${option.value}`}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
